/**
 * Utility to configure decimal conversion behavior for API requests
 */

/**
 * Centralized decimal precision configuration
 * Controls the number of decimal places used throughout the application
 */
export const ACCEPT_DECIMAL = 4;

export interface DecimalConversionConfig {
  /** Skip decimal conversion entirely for this request */
  skipDecimalConversion?: boolean;
  /** Array of field paths to EXCLUDE from conversion (keep raw values) */
  excludeDecimalFields?: string[];
}

// FIFO queue to store pending decimal configs
// This ensures the correct config is applied even when multiple requests are fired concurrently
const pendingDecimalConfigs: Array<DecimalConversionConfig | null> = [];

/**
 * Attach decimal config from promise to be used by the next axios request
 * This is called by handleRequest hooks before awaiting the promise
 */
export const attachDecimalConfigToRequest = (request: Promise<any>): void => {
  // Extract config from the promise
  const config = (request as any).__decimalConfig as DecimalConversionConfig | undefined;

  // Push config (or null placeholder) to maintain ordering with axios requests
  pendingDecimalConfigs.push(config ?? null);
};

/**
 * Get and consume the decimal config for the next request
 * Called by axios request interceptor to attach config to axios config object
 */
export const consumeNextRequestConfig = (): DecimalConversionConfig | null => {
  if (pendingDecimalConfigs.length === 0) {
    return null;
  }

  const config = pendingDecimalConfigs.shift();
  return config ?? null;
};

/**
 * Marks an API request to skip decimal conversion entirely
 * All decimal values will be preserved as raw values
 * 
 * @example
 * skipDecimalConversion(ParticipantService.getPersonalDetails())
 */
export const skipDecimalConversion = <T>(request: Promise<T>): Promise<T> => {
  // Attach metadata directly to the promise object
  (request as any).__decimalConfig = {
    skipDecimalConversion: true,
  } as DecimalConversionConfig;

  return request;
};

/**
 * Marks specific fields in an API response to skip decimal conversion
 * All other decimal values will be converted to ACCEPT_DECIMAL places
 * 
 * @param request - The API request promise
 * @param fields - Array of field paths to exclude (e.g., ['exchangeRate', 'items[].ratio'])
 * 
 * @example
 * excludeDecimalFields(
 *   TransactionService.getHistory(),
 *   ['exchangeRate', 'conversionRatio', 'items[].ratio']
 * )
 */
export const excludeDecimalFields = <T>(request: Promise<T>, fields: string[]): Promise<T> => {
  // Attach metadata directly to the promise object
  (request as any).__decimalConfig = {
    excludeDecimalFields: fields,
  } as DecimalConversionConfig;

  return request;
};

/**
 * Get decimal conversion config from an axios request config
 * Used internally by the API interceptor
 */
export const getDecimalConfig = (request: any): DecimalConversionConfig => {
  return request?.__decimalConfig || {};
};
